import { useState } from "react";
import { SectionTitle } from "@/components/section-title";
import { cases } from "../constants";
import type { CaseCategory } from "../types";

type FilterValue = "all" | CaseCategory;

const FILTERS: { label: string; value: FilterValue }[] = [
  { label: "Todos", value: "all" },
  { label: "Visão Computacional", value: "cv" },
  { label: "Agentes de IA", value: "agents" },
  { label: "MLOps / LLMOps", value: "mlops" },
];

const CATEGORY_META: Record<CaseCategory, { label: string; className: string }> = {
  cv: {
    label: "Visão Computacional",
    className: "border-cyan-500/20 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
  },
  agents: {
    label: "Agentes de IA",
    className: "border-purple-500/20 bg-purple-500/10 text-purple-600 dark:text-purple-400",
  },
  mlops: {
    label: "MLOps / LLMOps",
    className: "border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
};

const STATS = [
  { value: "30+", label: "projetos entregues" },
  { value: "3", label: "países atendidos" },
  { value: "55+", label: "câmeras simultâneas" },
  { value: "100k+", label: "processamentos/dia" },
];

export function Cases() {
  const [active, setActive] = useState<FilterValue>("all");

  const filtered = active === "all" ? cases : cases.filter((c) => c.category === active);
  const featured = filtered.filter((c) => c.featured);
  const regular = filtered.filter((c) => !c.featured);

  return (
    <section id="cases" className="flex flex-col gap-10">
      <div className="flex flex-col gap-8">
        <SectionTitle
          eyebrow="Resultados Reais"
          title="Cases de Sucesso"
          subtitle="Soluções de IA aplicadas em produção, da manufatura à saúde, do agronegócio às finanças."
        />

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 bg-slate-200 dark:bg-white/10">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center gap-1 bg-[#F4F7FB] dark:bg-[#09090C] px-6 py-5"
            >
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-500 to-purple-600 dark:from-cyan-300 dark:via-blue-200 dark:to-purple-300 bg-clip-text text-transparent">
                {stat.value}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 text-center">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setActive(f.value)}
              className={[
                "rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 border cursor-pointer",
                active === f.value
                  ? "border-slate-300 dark:border-white/20 bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-white"
                  : "border-slate-200 dark:border-white/8 bg-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-white/15",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured cases */}
      {featured.length > 0 && (
        <div className={`grid gap-4 ${featured.length === 1 ? "" : "md:grid-cols-2"}`}>
          {featured.map((c) => (
            <div
              key={c.id}
              className="relative flex flex-col gap-4 rounded-2xl border border-slate-200 dark:border-white/10 bg-gradient-to-br from-slate-50 to-white dark:from-white/5 dark:to-white/[0.02] p-6 overflow-hidden shadow-sm dark:shadow-none"
            >
              {/* Scale number — signature anchor */}
              {c.scale && (
                <div className="absolute right-5 top-4 text-right select-none pointer-events-none" aria-hidden>
                  <span className="text-5xl font-black leading-none bg-gradient-to-r from-cyan-300 via-blue-200 to-purple-300 bg-clip-text text-transparent opacity-20">
                    {c.scale}
                  </span>
                  {c.scaleSuffix && (
                    <p className="text-[10px] text-slate-500 tracking-wide mt-0.5">{c.scaleSuffix}</p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <span
                  className={`inline-flex w-fit items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${CATEGORY_META[c.category].className}`}
                >
                  {CATEGORY_META[c.category].label}
                </span>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white leading-snug pr-16">{c.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300/80 leading-relaxed">{c.result}</p>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                {c.tech.map((t) => (
                  <span
                    key={t}
                    className="rounded-md border border-slate-200 dark:border-white/8 bg-slate-100 dark:bg-white/5 px-2 py-0.5 text-[11px] text-slate-500 dark:text-slate-400"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regular cases grid */}
      {regular.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {regular.map((c) => (
            <div
              key={c.id}
              className="group flex flex-col gap-3 rounded-xl border border-slate-200 dark:border-white/8 bg-slate-50 dark:bg-white/[0.03] p-5 transition-all duration-200 hover:border-slate-300 dark:hover:border-white/15 hover:bg-white dark:hover:bg-white/[0.06] hover:scale-[1.01] shadow-sm dark:shadow-none"
            >
              <div className="flex flex-col gap-2">
                <span
                  className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${CATEGORY_META[c.category].className}`}
                >
                  {CATEGORY_META[c.category].label}
                </span>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white leading-snug">{c.title}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-3">{c.result}</p>
              </div>

              <div className="flex flex-wrap gap-1 mt-auto pt-1">
                {c.tech.slice(0, 4).map((t) => (
                  <span
                     key={t}
                    className="rounded border border-slate-200 dark:border-white/8 bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-500 dark:text-slate-500"
                  >
                    {t}
                  </span>
                ))}
                {c.tech.length > 4 && (
                  <span className="rounded border border-slate-200 dark:border-white/8 bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400 dark:text-slate-600">
                    +{c.tech.length - 4}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
